import { FunctionComponent, useContext, useEffect, useState } from 'react'
import { Button, MenuItem, Stack, TextField, Typography } from '@mui/material'
import { SwapHorizOutlined } from '@mui/icons-material'
import { DialogWrapper } from '../display-elements-components/dialog-wrapper'
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications'
import ReportActionService from '../../../shared/services/report-action/report-action.service'
import { IUserDetails } from '../../../shared/services/user-details/user-details.model'

// Bulk handover of the reporting period's incomplete actions from one owner to another, for
// when someone leaves or hands over their sites. Scoped to the period this table shows, and
// the dialog states exactly how many actions will move before anything happens. Completed
// actions keep their historical owner.
export const ReassignAllActionsDialog: FunctionComponent<{
  reportPeriodId: string,
  users: IUserDetails[],
  onReassigned: () => void,
}> = ({ reportPeriodId, users, onReassigned }) => {

  const userNotificationsContext = useContext(UserNotificationsContext)
  const reportActionService = new ReportActionService()
  const [open, setOpen] = useState(false)
  const [fromUserId, setFromUserId] = useState('')
  const [toUserId, setToUserId] = useState('')
  const [affectedCount, setAffectedCount] = useState<number | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const openHandler = () => {
    setFromUserId('')
    setToUserId('')
    setAffectedCount(null)
    setIsSubmitting(false)
    setOpen(true)
  }

  // Preview how many actions the handover will move: the period's outstanding plus upcoming
  // actions for the chosen owner, which is exactly the set the endpoint updates.
  useEffect(() => {
    if (!open || !fromUserId) {
      setAffectedCount(null)
      return
    }
    let cancelled = false
    Promise.all([
      reportActionService.getReportActionsWithPagination(reportPeriodId, `&userId=${fromUserId}&status=outstanding`, 0, 1),
      reportActionService.getReportActionsWithPagination(reportPeriodId, `&userId=${fromUserId}&status=upcoming`, 0, 1),
    ])
      .then(([outstanding, upcoming]) => {
        if (!cancelled) setAffectedCount(outstanding.count + upcoming.count)
      })
      .catch(() => { if (!cancelled) setAffectedCount(null) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fromUserId, reportPeriodId])

  const submitHandler = () => {
    if (!fromUserId || !toUserId || fromUserId === toUserId) return
    setIsSubmitting(true)
    reportActionService.reassignAllActions(reportPeriodId, fromUserId, toUserId === 'unassigned' ? null : toUserId)
      .then(response => {
        userNotificationsContext.openSnackbar({ open: true, message: `${response.updated ?? ''} ${response.updated === 1 ? 'task' : 'tasks'} reassigned`.trim() })
        setOpen(false)
        onReassigned()
      })
      .catch(err => {
        const message = err?.response?.data?.message || 'There was an error, please try again'
        userNotificationsContext.openSnackbar({ open: true, message, backgroundColor: 'text.primary' })
      })
      .finally(() => setIsSubmitting(false))
  }

  return (
    <>
      <Button variant='outlined' onClick={openHandler} startIcon={<SwapHorizOutlined fontSize='small' />}>
        <Typography sx={{ pl: 0.5 }}>Reassign owner</Typography>
      </Button>
      <DialogWrapper
        open={open}
        onClose={() => setOpen(false)}
        title='Reassign all tasks'
        cancelHandler={() => setOpen(false)}
        submitHandler={submitHandler}
        isSubmitting={isSubmitting}
        disableSaveButton={!fromUserId || !toUserId || fromUserId === toUserId || affectedCount === 0}
        submitButtonText='Reassign'
      >
        <Stack spacing={2}>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            Moves the incomplete tasks for this reporting period from one owner to another. Completed tasks keep their history.
          </Typography>
          <TextField
            select
            label='From'
            fullWidth
            value={fromUserId}
            onChange={(e) => setFromUserId(e.target.value)}
          >
            <MenuItem value='unassigned'>Unassigned</MenuItem>
            {users.map(user => (
              <MenuItem key={user.id} value={user.id}>{user.firstName} {user.surname}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label='To'
            fullWidth
            value={toUserId}
            onChange={(e) => setToUserId(e.target.value)}
          >
            <MenuItem value='unassigned'>Unassigned</MenuItem>
            {users.map(user => (
              <MenuItem key={user.id} value={user.id}>{user.firstName} {user.surname}</MenuItem>
            ))}
          </TextField>
          {fromUserId && affectedCount !== null &&
            <Typography variant='body2' sx={{ color: affectedCount === 0 ? 'text.secondary' : 'text.primary' }}>
              {affectedCount === 0
                ? 'No incomplete tasks to move for this owner in this period.'
                : `This will reassign ${affectedCount} ${affectedCount === 1 ? 'task' : 'tasks'} (outstanding and upcoming) in this reporting period.`}
            </Typography>}
        </Stack>
      </DialogWrapper>
    </>
  )
}
